import { useEffect, useRef, type ReactElement, type ReactNode } from "react";

interface Props {
  open: boolean;
  title?: string;
  description?: string;
  icon?: string;
  iconTone?: "accent" | "success" | "warning" | "danger";
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Defaults to "md" (440px). "lg" widens to 520px for forms with more fields, "xl" to 680px. */
  size?: "md" | "lg" | "xl";
}

const TONE_BG: Record<NonNullable<Props["iconTone"]>, string> = {
  accent: "bg-accent-bg text-accent",
  success: "bg-success-bg text-success",
  warning: "bg-warning-bg text-warning",
  danger: "bg-danger-bg text-danger",
};

function useDialogToggle(open: boolean, ref: React.RefObject<HTMLDialogElement | null>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open, ref]);
}

const WIDTHS = {
  md: "w-full max-w-[440px]",
  lg: "w-full max-w-[520px]",
  xl: "w-full max-w-[680px]",
} as const;

export function Modal(props: Readonly<Props>): ReactElement | null {
  const { open, onClose, children, footer, size = "md" } = props;
  const ref = useRef<HTMLDialogElement>(null);
  useDialogToggle(open, ref);
  if (!open) return null;
  const widthCls = WIDTHS[size];
  const className =
    "rounded-xl border border-line bg-surface-1 p-0 text-ink backdrop:bg-black/40 " + widthCls;
  return (
    <dialog ref={ref} onClose={onClose} className={className}>
      <div className="p-6">
        <ModalHeader
          {...(props.title !== undefined ? { title: props.title } : {})}
          {...(props.description !== undefined ? { description: props.description } : {})}
          {...(props.icon !== undefined ? { icon: props.icon } : {})}
          iconTone={props.iconTone ?? "accent"}
        />
        {children}
        {footer !== undefined && (
          <div className="mt-5 flex flex-wrap justify-end gap-2">{footer}</div>
        )}
      </div>
    </dialog>
  );
}

interface HeaderProps {
  title?: string;
  description?: string;
  icon?: string;
  iconTone: NonNullable<Props["iconTone"]>;
}

function ModalHeader({
  title,
  description,
  icon,
  iconTone,
}: Readonly<HeaderProps>): ReactElement | null {
  if (title === undefined && icon === undefined) return null;
  return (
    <div className="mb-4 flex items-start gap-3">
      {icon !== undefined && (
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${TONE_BG[iconTone]}`}
        >
          <i className={`ti ${icon} text-lg`} />
        </div>
      )}
      {title !== undefined && (
        <div className="flex-1">
          <h3 className="mb-1">{title}</h3>
          {description !== undefined && <p className="text-sm text-ink-secondary">{description}</p>}
        </div>
      )}
    </div>
  );
}
